import { useRef, useState, useCallback } from 'react';
import RecordRTC from 'recordrtc';
import { initLiveSession, clearStoredWebSocketUrl } from '@/lib/voice-helper';

// Add these types at the top
declare global {
  interface Window {
    audioContext: AudioContext;
    soundMeter: any;
  }
}

// Add this SoundMeter class before the hook
class SoundMeter {
  private context: AudioContext;
  private analyser: AnalyserNode;
  private mic: MediaStreamAudioSourceNode | null;
  private volumes: Uint8Array;
  public instant: number;

  constructor(context: AudioContext) {
    this.context = context;
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.minDecibels = -127;
    this.analyser.maxDecibels = 0;
    this.analyser.smoothingTimeConstant = 0.4;
    this.volumes = new Uint8Array(this.analyser.frequencyBinCount);
    this.mic = null;
    this.instant = 0;
  }

  connectToSource(stream: MediaStream, callback: (error?: Error) => void) {
    try {
      this.mic = this.context.createMediaStreamSource(stream);
      this.mic.connect(this.analyser);
      callback();
    } catch (e) {
      callback(
        e instanceof Error ? e : new Error('Error connecting to source')
      );
    }
  }

  measureVolume() {
    this.analyser.getByteFrequencyData(this.volumes);
    let volumeSum = 0;
    for (const volume of this.volumes) {
      volumeSum += volume;
    }
    this.instant = volumeSum / this.volumes.length;
    // Convert to percentage (0-100)
    return (this.instant * 100) / 127;
  }

  stop() {
    if (this.mic) {
      this.mic.disconnect();
      this.mic = null;
    }
    this.instant = 0;
  }
}

interface UseVoiceRecordingProps {
  onTranscriptReceived: (transcript: string) => void;
  silenceThreshold?: number; // Optional prop to configure silence threshold
  silenceDuration?: number; // Optional prop to configure silence duration
  handleSend: () => void;
}

export const useVoiceRecording = ({
  onTranscriptReceived,
  silenceThreshold = 15, // Adjusted threshold for new scale (0-100)
  silenceDuration = 3000,
  handleSend,
}: UseVoiceRecordingProps) => {
  const socketRef = useRef<WebSocket | null>(null);
  const recorderRef = useRef<RecordRTC | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastAudioLevelRef = useRef<number>(0);
  const soundMeterRef = useRef<any>(null);
  const volumeCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const cleanup = useCallback(() => {
    console.log('Running cleanup...');

    if (socketRef.current) {
      if (socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close();
      }
      socketRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    if (volumeCheckIntervalRef.current) {
      clearInterval(volumeCheckIntervalRef.current);
      volumeCheckIntervalRef.current = null;
    }

    if (soundMeterRef.current) {
      soundMeterRef.current.stop();
      soundMeterRef.current = null;
    }

    if (window.audioContext) {
      window.audioContext.close().catch(console.error);
      // window.audioContext = undefined;
    }

    setIsListening(false);
    setIsProcessing(false);
    setAudioLevel(0);
    setIsConnecting(false);
  }, []);

  const initializeRecording = (stream: MediaStream, isSafari: boolean) => {
    console.log('Initializing RecordRTC...');
    const recorderConfig = {
      type: 'audio',
      mimeType: isSafari ? 'audio/wav' : 'audio/webm;codecs=pcm',
      recorderType: RecordRTC.StereoAudioRecorder,
      timeSlice: 1000,
      desiredSampRate: 48000,
      numberOfAudioChannels: 1,
      bufferSize: 16384,
      ondataavailable: async (blob: Blob) => {
        if (blob.size > 0) {
          console.log('Audio data available, size:', blob.size);
          try {
            const buffer = await blob.arrayBuffer();
            // Only remove WAV header for non-Safari browsers
            const modifiedBuffer = isSafari ? buffer : buffer.slice(44);
            if (socketRef.current?.readyState === WebSocket.OPEN) {
              socketRef.current.send(modifiedBuffer);
              console.log('Audio data sent to WebSocket');
            }
          } catch (error) {
            console.error('Error processing audio data:', error);
          }
        }
      },
    };

    recorderRef.current = new RecordRTC(
      stream,
      recorderConfig as RecordRTC.Options
    );

    // Start the recording
    recorderRef.current.startRecording();
    console.log('RecordRTC recording started');

    try {
      window.audioContext =
        window.audioContext ||
        new (window.AudioContext || (window as any).webkitAudioContext)();
      soundMeterRef.current = new SoundMeter(window.audioContext);

      soundMeterRef.current.connectToSource(stream, (error?: Error) => {
        if (error) {
          console.error('Error connecting to source:', error);
          return;
        }

        // Start volume monitoring
        volumeCheckIntervalRef.current = setInterval(() => {
          const volume = soundMeterRef.current.measureVolume();
          setAudioLevel(volume);
          handleSilenceDetection(volume);
        }, 100);
      });
    } catch (error) {
      console.warn('Audio monitoring not supported:', error);
    }

    setIsListening(true);
    setIsConnecting(false);
  };

  const startRecording = async () => {
    try {
      setIsConnecting(true);
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      console.log('Requesting audio stream...');
      let stream;
      try {
        const audioConstraints = isSafari
          ? {
              audio: {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
              },
              video: false,
            }
          : {
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
                sampleRate: 48000,
              },
              video: false,
            };

        stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
        console.log('Audio stream obtained');
        streamRef.current = stream;
      } catch (mediaError: any) {
        if (
          mediaError.name === 'NotAllowedError' ||
          mediaError.name === 'PermissionDeniedError'
        ) {
          throw new Error(
            'Microphone permission was denied. Please allow microphone access to use voice input.'
          );
        }
        throw mediaError;
      }

      console.log('Initializing live session...');
      const { url } = await initLiveSession();
      console.log('WebSocket URL received:', url);

      socketRef.current = new WebSocket(url);

      socketRef.current.onopen = () => {
        console.log('WebSocket connection established');
        initializeRecording(stream, isSafari);
      };

      socketRef.current.onmessage = (event) => {
        const message = JSON.parse(event.data);
        console.log('Received WebSocket message:', message);
        if (message?.type === 'transcript') {
          if (message.data.is_final) {
            console.log(
              'Final transcript received:',
              message.data.utterance.text
            );
            onTranscriptReceived(message.data.utterance.text);
          }
        }
      };

      socketRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error: any) {
      console.error('Error starting recording:', error);
      alert(
        error.message || 'Failed to start voice recording. Please try again.'
      );

      cleanup();
    }
  };

  const stopRecording = useCallback(() => {
    console.log('Stopping recording...');
    setIsProcessing(true);

    // First, stop all audio monitoring
    if (volumeCheckIntervalRef.current) {
      clearInterval(volumeCheckIntervalRef.current);
      volumeCheckIntervalRef.current = null;
    }

    if (soundMeterRef.current) {
      soundMeterRef.current.stop();
      soundMeterRef.current = null;
    }

    // Stop the recorder first
    if (recorderRef.current) {
      recorderRef.current.stopRecording(() => {
        console.log('RecordRTC recording stopped');
        const lastRecorder = recorderRef.current;
        recorderRef.current = null;
        lastRecorder?.destroy();
      });
    }

    // Stop the media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
        console.log('Audio track stopped');
      });
      streamRef.current = null;
    }

    let hasReceivedFinalTranscript = false;

    // Handle WebSocket cleanup
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      console.log('WebSocket is open, sending stop message...');

      const stopMessage = JSON.stringify({ type: 'stop_recording' });
      socketRef.current.send(stopMessage);
      console.log('Sent stop message:', stopMessage);

      // Set up a shorter timeout for final transcript
      const finalTranscriptTimeout = setTimeout(() => {
        if (!hasReceivedFinalTranscript) {
          console.log('No final transcript received, proceeding with cleanup');
          cleanup();
        }
      }, 1000);

      const messageHandler = (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data);
          if (message?.type === 'transcript' && message.data.is_final) {
            hasReceivedFinalTranscript = true;
            clearTimeout(finalTranscriptTimeout);
            onTranscriptReceived(message.data.utterance.text);
            cleanup();
          }
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
          clearTimeout(finalTranscriptTimeout);
          cleanup();
        }
      };

      socketRef.current.onmessage = messageHandler;
    } else {
      console.log('WebSocket is not open. Proceeding with cleanup.');
      cleanup();
    }

    clearStoredWebSocketUrl();
  }, [onTranscriptReceived, cleanup]);

  const handleSilenceDetection = useCallback(
    (volumeLevel: number) => {
      lastAudioLevelRef.current = volumeLevel;

      // Log every volume level for debugging
      console.log(
        'Current volume level:',
        volumeLevel.toFixed(1),
        'Threshold:',
        silenceThreshold
      );

      if (volumeLevel < silenceThreshold) {
        // Only create timer if one doesn't exist AND we're still listening
        if (!silenceTimeoutRef.current) {
          console.log(
            '🔇 Volume level below threshold, starting silence timer'
          );
          silenceTimeoutRef.current = setTimeout(() => {
            console.log('Checking final volume:', lastAudioLevelRef.current);
            // Double check we're still listening and still quiet
            if (lastAudioLevelRef.current < silenceThreshold) {
              console.log('🛑 Silence confirmed - stopping recording');
              stopRecording();
            } else {
              console.log(
                'Volume recovered during timeout, continuing recording'
              );
            }
            silenceTimeoutRef.current = null;
          }, silenceDuration);
        }
      } else {
        // If volume is above threshold and we have a timer, clear it
        if (silenceTimeoutRef.current) {
          console.log('🔊 Volume recovered, clearing silence timer');
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
      }
    },
    [silenceThreshold, silenceDuration, stopRecording]
  );

  return {
    isListening,
    isProcessing,
    isConnecting,
    audioLevel,
    startRecording,
    stopRecording,
    cleanup,
  };
};
